import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { Canon } from '@models/Canon';
import { ALL_BOOK_CODES, CANON_NAMES, CanonData, getCanon } from '@models/Canons';
import { ProjectConfiguration } from '@models/ProjectConfiguration';
import { UbsBook } from '@models/UbsBook';
import { VerseReference } from '@models/VerseReference';
import { ProjectTextStyleDirective } from 'src/app/project-text-style.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-canons',
  imports: [
    CommonModule,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatExpansionModule,
    ReactiveFormsModule,
    MatButtonModule,
    ProjectTextStyleDirective
  ],
  templateUrl: './canons.component.html',
  styleUrl: './../project-settings.component.scss'
})
export class CanonsComponent implements OnInit {
  @Input() project!: ProjectConfiguration;
  @Input() formGroup!: FormGroup;
  @Input() resetEvent!: EventEmitter<void>;

  /// NB I'm just including OT and NT for now, since we don't have LXX support yet.
  canonNames: Canon[] = ['OT', 'NT'] // CANON_NAMES;

  constructor(private fb: FormBuilder) { }

  ngOnInit() {

    this.formGroup.addControl('canons', new FormControl(this.project.canons));

    /// add controls for the frequency thresholds
    for (const c of CANON_NAMES) {
      this.formGroup.addControl(
        c,
        this.fb.control(this.project.getFrequencyThreshold(c), {
          validators: (control) => {
            const value = control.value;
            return Number.isInteger(value) && value > 0 ? null : { positiveInteger: true };
          }
        })
      );
    }

    /// add controls for the book names
    for (const b of ALL_BOOK_CODES) {
      this.formGroup.addControl(b, this.fb.control(this.project.getBookName(b), [Validators.required]));
    }

    this.resetEvent.subscribe(() => {
      /// reset the canons to the project settings
      this.formGroup.get('canons')?.setValue(this.project.canons);

      /// reset the form group to the project settings
      for (const c of CANON_NAMES) {
        this.formGroup.get(c)?.setValue(this.project.getFrequencyThreshold(c));
      }
      for (const b of ALL_BOOK_CODES) {
        this.formGroup.get(b)?.setValue(this.project.getBookName(b));
      }
    });

  }

  get canons(): Canon[] {
    return this.formGroup.get('canons')?.value || [];
  }

  canonData(canon: Canon): CanonData {
    return getCanon(canon);
  }

  latinBookName(book: UbsBook): string {
    return VerseReference.ubsBookToLatin(book);
  }

  englishBookName(book: UbsBook): string {
    return VerseReference.ubsBookToEnglish(book);
  }

  autofillEnglish(canon: Canon) {
    const patch: { [key: string]: string } = {};
    getCanon(canon).books.forEach((book) => {
      patch[book] = VerseReference.ubsBookToEnglish(book);
    });
    this.formGroup.patchValue(patch);
  }

  autofillLatin(canon: Canon) {
    const patch: { [key: string]: string } = {};
    getCanon(canon).books.forEach((book) => {
      patch[book] = VerseReference.ubsBookToLatin(book);
    });
    this.formGroup.patchValue(patch);
  }

}
